import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  RefreshCw,
  Eye,
  Trash2,
  AlertCircle,
  Loader2,
  X,
  CheckCircle,
  DollarSign,
  User,
  Building2,
  Mail as MailIcon,
  Flag,
  AlertTriangle,
  Forward,
  Reply,
  Paperclip,
  Monitor,
  Inbox,
  Send,
  ArchiveIcon,
  TrendingUp,
  CheckCheck,
  MessageCircle as MessageCircleIcon,
  AlertOctagon,
  HelpCircle,
  CircleX,
  Calendar,
} from "lucide-react";
import { messageService } from "../../api/services/messageService";
import { format, formatDistance } from "date-fns";
import { useToast } from "../../components/common/ThemeToaster";

// Helper Functions
const getCategoryInfo = (category) => {
  const categories = {
    general: {
      icon: HelpCircle,
      label: "General",
      color: "text-gray-500",
      bg: "bg-gray-100 dark:bg-gray-800",
      border: "border-gray-200 dark:border-gray-700",
    },
    support: {
      icon: HeadphonesIcon,
      label: "Support",
      color: "text-blue-500",
      bg: "bg-blue-50 dark:bg-blue-900/20",
      border: "border-blue-200 dark:border-blue-800",
    },
    sales: {
      icon: TrendingUp,
      label: "Sales",
      color: "text-green-500",
      bg: "bg-green-50 dark:bg-green-900/20",
      border: "border-green-200 dark:border-green-800",
    },
    billing: {
      icon: DollarSign,
      label: "Billing",
      color: "text-yellow-500",
      bg: "bg-yellow-50 dark:bg-yellow-900/20",
      border: "border-yellow-200 dark:border-yellow-800",
    },
    technical: {
      icon: Monitor,
      label: "Technical",
      color: "text-purple-500",
      bg: "bg-purple-50 dark:bg-purple-900/20",
      border: "border-purple-200 dark:border-purple-800",
    },
    feedback: {
      icon: MessageCircleIcon,
      label: "Feedback",
      color: "text-pink-500",
      bg: "bg-pink-50 dark:bg-pink-900/20",
      border: "border-pink-200 dark:border-pink-800",
    },
    complaint: {
      icon: AlertOctagon,
      label: "Complaint",
      color: "text-red-500",
      bg: "bg-red-50 dark:bg-red-900/20",
      border: "border-red-200 dark:border-red-800",
    },
    other: {
      icon: MailIcon,
      label: "Other",
      color: "text-gray-400",
      bg: "bg-gray-100 dark:bg-gray-800",
      border: "border-gray-200 dark:border-gray-700",
    },
  };
  return categories[category] || categories.general;
};

const getPriorityInfo = (priority) => {
  const priorities = {
    low: {
      icon: Flag,
      label: "Low",
      color: "text-green-500",
      bg: "bg-green-50 dark:bg-green-900/20",
      border: "border-green-200 dark:border-green-800",
    },
    normal: {
      icon: Flag,
      label: "Normal",
      color: "text-blue-500",
      bg: "bg-blue-50 dark:bg-blue-900/20",
      border: "border-blue-200 dark:border-blue-800",
    },
    high: {
      icon: Flag,
      label: "High",
      color: "text-orange-500",
      bg: "bg-orange-50 dark:bg-orange-900/20",
      border: "border-orange-200 dark:border-orange-800",
    },
    urgent: {
      icon: AlertTriangle,
      label: "Urgent",
      color: "text-red-500",
      bg: "bg-red-50 dark:bg-red-900/20",
      border: "border-red-200 dark:border-red-800",
    },
  };
  return priorities[priority] || priorities.normal;
};

const getStatusInfo = (status) => {
  const statuses = {
    new: {
      icon: Inbox,
      label: "New",
      color: "text-blue-500",
      bg: "bg-blue-50 dark:bg-blue-900/20",
      border: "border-blue-200 dark:border-blue-800",
    },
    read: {
      icon: CheckCircle,
      label: "Read",
      color: "text-gray-500",
      bg: "bg-gray-100 dark:bg-gray-800",
      border: "border-gray-200 dark:border-gray-700",
    },
    replied: {
      icon: Reply,
      label: "Replied",
      color: "text-green-500",
      bg: "bg-green-50 dark:bg-green-900/20",
      border: "border-green-200 dark:border-green-800",
    },
    forwarded: {
      icon: Forward,
      label: "Forwarded",
      color: "text-purple-500",
      bg: "bg-purple-50 dark:bg-purple-900/20",
      border: "border-purple-200 dark:border-purple-800",
    },
    resolved: {
      icon: CheckCheck,
      label: "Resolved",
      color: "text-emerald-500",
      bg: "bg-emerald-50 dark:bg-emerald-900/20",
      border: "border-emerald-200 dark:border-emerald-800",
    },
    archived: {
      icon: ArchiveIcon,
      label: "Archived",
      color: "text-gray-500",
      bg: "bg-gray-100 dark:bg-gray-800",
      border: "border-gray-200 dark:border-gray-700",
    },
    spam: {
      icon: CircleX,
      label: "Spam",
      color: "text-red-500",
      bg: "bg-red-50 dark:bg-red-900/20",
      border: "border-red-200 dark:border-red-800",
    },
  };
  return statuses[status] || statuses.new;
};

const formatTime = (date) => {
  return formatDistance(new Date(date), new Date(), { addSuffix: true });
};

// Headphones Icon Component
const HeadphonesIcon = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z" />
    <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
  </svg>
);

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, trend }) => (
  <motion.div
    whileHover={{ y: -2, scale: 1.02 }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
    className="relative overflow-hidden bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-white/50 dark:border-gray-800/50 shadow-lg"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 opacity-5" />
    <div className="relative p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-md">
          <Icon size={20} className="text-white" />
        </div>
        <span className="text-2xl font-bold text-gray-900 dark:text-white">
          {value?.toLocaleString() || 0}
        </span>
      </div>
      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
        {label}
      </p>
      {trend && (
        <div className="flex items-center gap-1 mt-2 text-xs">
          <TrendingUp size={12} className="text-emerald-500" />
          <span className="text-emerald-600 dark:text-emerald-400">
            +{trend}%
          </span>
          <span className="text-gray-500">vs last week</span>
        </div>
      )}
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 " />
  </motion.div>
);

// Message Card Component
const MessageCard = ({
  message,
  isSelected,
  onSelect,
  onViewDetails,
  onReply,
  onMarkAsRead,
  onArchive,
  onDelete,
}) => {
  const category = getCategoryInfo(message.category);
  const priority = getPriorityInfo(message.priority);
  const status = getStatusInfo(message.status);
  const CategoryIcon = category.icon;
  const PriorityIcon = priority.icon;
  const StatusIcon = status.icon;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ y: -2 }}
      transition={{ type: "spring", stiffness: 300 }}
      className={`group bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border ${
        !message.isRead
          ? "border-primary-200 dark:border-primary-800 bg-gradient-to-r from-primary-50/30 to-white dark:from-primary-900/10 dark:to-gray-900"
          : "border-gray-200/50 dark:border-gray-800/50"
      } shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden`}
    >
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 pt-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onSelect(message.id)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${status.bg} ${status.color} border ${status.border}`}
                  >
                    <StatusIcon size={10} /> {status.label}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${priority.bg} ${priority.color} border ${priority.border}`}
                  >
                    <PriorityIcon size={10} /> {priority.label}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${category.bg} ${category.color} border ${category.border}`}
                  >
                    <CategoryIcon size={10} /> {category.label}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                  {message.subject}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                  {message.content}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <User size={12} /> {message.name}
                  </span>
                  <span className="flex items-center gap-1">
                    <MailIcon size={12} /> {message.email}
                  </span>
                  {message.company && (
                    <span className="flex items-center gap-1">
                      <Building2 size={12} /> {message.company}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar size={12} /> {formatTime(message.createdAt)}
                  </span>
                </div>
                {message.attachments && message.attachments.length > 0 && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                    <Paperclip size={12} />{" "}
                    <span>{message.attachments.length} attachment(s)</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => onViewDetails(message)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="View Details"
                >
                  <Eye size={14} className="text-blue-500" />
                </button>
                <button
                  onClick={() => onReply(message)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="Reply"
                >
                  <Reply size={14} className="text-green-500" />
                </button>
                <button
                  onClick={() => onMarkAsRead(message.id, message.isRead)}
                  className={`p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${message.isRead ? "text-green-600" : "text-gray-400"}`}
                  title={message.isRead ? "Mark as Unread" : "Mark as Read"}
                >
                  <MailIcon size={14} />
                </button>
                <button
                  onClick={() => onArchive(message.id)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="Archive"
                >
                  <ArchiveIcon size={14} className="text-gray-500" />
                </button>
                <button
                  onClick={() => onDelete(message.id)}
                  className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  title="Delete"
                >
                  <Trash2 size={14} className="text-red-500" />
                </button>
              </div>
            </div>
            {message.adminResponse && (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2 text-xs">
                  <CheckCheck size={12} className="text-green-500" />{" "}
                  <span className="text-gray-500">Admin responded</span>{" "}
                  <span className="text-gray-400">•</span>{" "}
                  <span className="text-gray-500">
                    {formatTime(message.responseSentAt)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Details Modal Component
const DetailsModal = ({ message, onClose, onReply, onMarkAsRead }) => {
  const category = getCategoryInfo(message.category);
  const priority = getPriorityInfo(message.priority);
  const status = getStatusInfo(message.status);
  const CategoryIcon = category.icon;
  const PriorityIcon = priority.icon;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 p-5 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Message Details
            </h2>
            <p className="text-sm font-mono text-primary-600 dark:text-primary-400 mt-0.5">
              {message.messageNumber}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex flex-wrap gap-3">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full ${status.bg} ${status.color} border ${status.border}`}
            >
              <StatusIcon size={14} /> {status.label}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full ${priority.bg} ${priority.color} border ${priority.border}`}
            >
              <PriorityIcon size={14} /> {priority.label}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full ${category.bg} ${category.color} border ${category.border}`}
            >
              <CategoryIcon size={14} /> {category.label}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl">
            <div>
              <p className="text-xs text-gray-500">Name</p>
              <p className="text-sm font-medium">{message.name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Email</p>
              <p className="text-sm">{message.email}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Phone</p>
              <p className="text-sm">{message.phone || "N/A"}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Company</p>
              <p className="text-sm">{message.company || "N/A"}</p>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold mb-2">Subject</h3>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {message.subject}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold mb-2">Message</h3>
            <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-xl p-4 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
              {message.content}
            </div>
          </div>
          {message.attachments && message.attachments.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Attachments</h3>
              <div className="flex flex-wrap gap-2">
                {message.attachments.map((att, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <Paperclip size={14} />
                    <span className="text-sm">{att}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {message.adminResponse && (
            <div className="p-4 bg-primary-50/50 dark:bg-primary-900/20 rounded-xl">
              <h3 className="text-sm font-semibold text-primary-600 mb-2 flex items-center gap-2">
                <Reply size={14} /> Admin Response
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {message.adminResponse}
              </p>
              {message.responseSentAt && (
                <p className="text-xs text-gray-500 mt-2">
                  Sent:{" "}
                  {format(
                    new Date(message.responseSentAt),
                    "MMM d, yyyy HH:mm",
                  )}
                </p>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500">
            <div>
              <span className="block">Created</span>
              <span className="text-gray-900 dark:text-white">
                {format(new Date(message.createdAt), "MMM d, yyyy HH:mm")}
              </span>
            </div>
            {message.readAt && (
              <div>
                <span className="block">Read</span>
                <span className="text-gray-900 dark:text-white">
                  {format(new Date(message.readAt), "MMM d, yyyy HH:mm")}
                </span>
              </div>
            )}
            {message.source && (
              <div>
                <span className="block">Source</span>
                <span className="text-gray-900 dark:text-white">
                  {message.source}
                </span>
              </div>
            )}
            {message.ipAddress && (
              <div>
                <span className="block">IP Address</span>
                <span className="text-gray-900 dark:text-white">
                  {message.ipAddress}
                </span>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Reply Modal Component
const ReplyModal = ({ message, onClose, onSuccess }) => {
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const { success: toastSuccess, error: toastError } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!response.trim()) {
      setError("Response message is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Save response to database
      await messageService.addResponse(message.id, response);
      await messageService.markAsRead(message.id);

      // Send email to customer if email exists and toggle is on
      if (sendEmail && message.email) {
        try {
          await messageService.sendResponseEmail(
            message.id,
            response,
            message.email,
          );
          toastSuccess("Reply sent and email delivered successfully!");
        } catch (emailError) {
          console.error("Error sending email:", emailError);
          toastSuccess("Reply saved, but email sending failed");
        }
      } else {
        toastSuccess("Reply sent successfully");
      }

      onSuccess();
    } catch (err) {
      console.error("Error submitting reply:", err);
      toastError(err.message || "Failed to send reply");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Reply to {message.name}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {message.email}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Customer Email Info */}
        {message.email && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Customer Email
            </p>
            <div className="flex items-center gap-2">
              <MailIcon size={14} className="text-blue-500" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {message.email}
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl text-red-600 dark:text-red-400 text-sm flex items-center gap-2">
            <AlertCircle size={14} />
            {error}
          </div>
        )}

        <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <p className="text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
            Original Message:
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
            {message.content}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Response Message <span className="text-red-500">*</span>
            </label>
            <textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              rows={6}
              className="w-full p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
              placeholder="Type your response to the customer..."
              required
            />
          </div>

          {/* Email Toggle */}
          {message.email && (
            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
              <label className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-2">
                  <MailIcon size={16} className="text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Send Email to Customer
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Customer will receive this reply via email
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSendEmail(!sendEmail)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ${
                    sendEmail
                      ? "bg-primary-600"
                      : "bg-gray-200 dark:bg-gray-700"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                      sendEmail ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </label>
            </div>
          )}

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2 hover:from-primary-600 hover:to-secondary-600 transition-all shadow-lg"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : sendEmail ? (
                <>
                  <Send size={18} />
                  Send & Email
                </>
              ) : (
                <>
                  <MessageCircleIcon size={18} />
                  Save Reply
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Main Component
const MessagesManagement = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);
  const { success: toastSuccess, error: toastError } = useToast();

  useEffect(() => {
    fetchMessages();
    fetchStats();
  }, [
    statusFilter,
    priorityFilter,
    categoryFilter,
    sortBy,
    sortOrder,
    currentPage,
    searchTerm,
  ]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        sortBy,
        sortOrder,
        ...(statusFilter !== "all" && { status: statusFilter }),
        ...(priorityFilter !== "all" && { priority: priorityFilter }),
        ...(categoryFilter !== "all" && { category: categoryFilter }),
        ...(searchTerm && { search: searchTerm }),
      };
      const response = await messageService.getMessages(params);
      let messagesData = [];
      if (response?.data?.data) messagesData = response.data.data;
      else if (response?.data?.messages) messagesData = response.data.messages;
      else if (Array.isArray(response?.data)) messagesData = response.data;
      else if (response?.messages) messagesData = response.messages;
      setMessages(messagesData || []);
      setTotalMessages(
        response?.data?.pagination?.total || messagesData.length,
      );
      setTotalPages(response?.data?.pagination?.totalPages || 1);
    } catch (err) {
      toastError(err.message || "Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await messageService.getMessageStats();

      // Extract stats from the correct nested structure
      let statsData = null;
      if (response?.data?.stats) {
        statsData = response.data.stats;
      } else if (response?.data?.data?.stats) {
        statsData = response.data.data.stats;
      } else if (response?.stats) {
        statsData = response.stats;
      } else if (response?.data) {
        statsData = response.data;
      }

      if (statsData) {
        setStats({
          total: statsData.total || 0,
          unread: statsData.unread || 0,
          replied: statsData.byStatus?.replied || 0,
          urgent: statsData.byPriority?.urgent || 0,
          archived: statsData.byStatus?.archived || 0,
          spam: statsData.byStatus?.spam || 0,
          assigned: statsData.assigned || 0,
        });
      } else {
        setStats({
          total: 0,
          unread: 0,
          replied: 0,
          urgent: 0,
          archived: 0,
          spam: 0,
          assigned: 0,
        });
      }
    } catch (err) {
      setStats({
        total: 0,
        unread: 0,
        replied: 0,
        urgent: 0,
        archived: 0,
        spam: 0,
        assigned: 0,
      });
    }
  };

  const handleMarkAsRead = async (id, isRead) => {
    try {
      if (!isRead) await messageService.markAsRead(id);
      else await messageService.markAsUnread(id);
      fetchMessages();
      fetchStats();
    } catch (err) {
      toastError("Failed to update status");
    }
  };

  const handleArchive = async (id) => {
    try {
      await messageService.archiveMessage(id);
      toastSuccess("Message archived");
      fetchMessages();
      fetchStats();
    } catch (err) {
      toastError("Failed to archive");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await messageService.deleteMessage(id);
      toastSuccess("Message deleted");
      fetchMessages();
      fetchStats();
      setSelectedMessages((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    } catch (err) {
      toastError("Failed to delete");
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedMessages.size) return;
    if (!window.confirm(`Delete ${selectedMessages.size} message(s)?`)) return;
    try {
      await messageService.bulkDeleteMessages(Array.from(selectedMessages));
      toastSuccess(`${selectedMessages.size} message(s) deleted`);
      setSelectedMessages(new Set());
      setSelectAll(false);
      fetchMessages();
      fetchStats();
    } catch (err) {
      toastError("Failed to delete messages");
    }
  };

  const handleBulkArchive = async () => {
    if (!selectedMessages.size) return;
    try {
      await messageService.bulkArchive(Array.from(selectedMessages));
      toastSuccess(`${selectedMessages.size} message(s) archived`);
      setSelectedMessages(new Set());
      setSelectAll(false);
      fetchMessages();
      fetchStats();
    } catch (err) {
      toastError("Failed to archive messages");
    }
  };

  const handleBulkRead = async (read) => {
    if (!selectedMessages.size) return;
    try {
      if (read)
        await messageService.bulkMarkAsRead(Array.from(selectedMessages));
      else
        await messageService.bulkUpdateStatus(
          Array.from(selectedMessages),
          "read",
        );
      toastSuccess(`${selectedMessages.size} message(s) updated`);
      setSelectedMessages(new Set());
      setSelectAll(false);
      fetchMessages();
      fetchStats();
    } catch (err) {
      toastError("Failed to update messages");
    }
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    setSelectedMessages(
      selectAll ? new Set() : new Set(messages.map((m) => m.id)),
    );
  };
  const handleSelectMessage = (id) => {
    const newSet = new Set(selectedMessages);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    setSelectedMessages(newSet);
    setSelectAll(newSet.size === messages.length);
  };

  const statCards = [
    { icon: Inbox, label: "Total Messages", value: stats?.total, trend: 8 },
    { icon: MailIcon, label: "Unread", value: stats?.unread },
    { icon: Reply, label: "Replied", value: stats?.replied },
    { icon: AlertTriangle, label: "Urgent", value: stats?.urgent },
    { icon: ArchiveIcon, label: "Archived", value: stats?.archived },
  ];

  if (loading && !messages.length)
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2
            size={48}
            className="animate-spin text-primary-500 mx-auto mb-4"
          />
          <p className="text-gray-500">Loading messages...</p>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-6 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      <div className="space-y-6 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 bg-clip-text text-transparent">
              Messages
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage customer inquiries and messages
            </p>
          </div>
          <button
            onClick={fetchMessages}
            className="px-4 py-2 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border rounded-xl flex items-center gap-2"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />{" "}
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {statCards.map((card, i) => (
            <StatCard
              key={i}
              icon={card.icon}
              label={card.label}
              value={card.value}
              trend={card.trend}
            />
          ))}
        </div>

        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search by name, email, subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="replied">Replied</option>
              <option value="resolved">Resolved</option>
              <option value="archived">Archived</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Priority</option>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Category</option>
              <option value="general">General</option>
              <option value="support">Support</option>
              <option value="sales">Sales</option>
              <option value="billing">Billing</option>
              <option value="technical">Technical</option>
              <option value="feedback">Feedback</option>
              <option value="complaint">Complaint</option>
            </select>
          </div>
        </div>

        {selectedMessages.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3"
          >
            <span className="text-sm font-semibold">
              {selectedMessages.size} selected
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleBulkRead(true)}
                className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg"
              >
                Mark Read
              </button>
              <button
                onClick={handleBulkArchive}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg"
              >
                Archive
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg"
              >
                Delete
              </button>
            </div>
          </motion.div>
        )}

        {error && (
          <div className="p-4 bg-red-50 rounded-2xl flex items-center gap-3">
            <AlertCircle className="text-red-500" />
            <p className="text-red-600 text-sm">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto">
              <X size={16} />
            </button>
          </div>
        )}

        <div className="space-y-3">
          <AnimatePresence>
            {messages.map((msg) => (
              <MessageCard
                key={msg.id}
                message={msg}
                isSelected={selectedMessages.has(msg.id)}
                onSelect={handleSelectMessage}
                onViewDetails={(m) => {
                  setSelectedMessage(m);
                  setShowDetailsModal(true);
                }}
                onReply={(m) => {
                  setSelectedMessage(m);
                  setShowReplyModal(true);
                }}
                onMarkAsRead={handleMarkAsRead}
                onArchive={handleArchive}
                onDelete={handleDelete}
              />
            ))}
          </AnimatePresence>
        </div>

        {!messages.length && !loading && (
          <div className="text-center py-12 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border">
            <Inbox size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold mb-2">No messages found</h3>
            <p className="text-gray-500">
              {searchTerm ? "Try adjusting your search" : "Your inbox is empty"}
            </p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-50"
            >
              Prev
            </button>
            <span className="px-3 py-1.5">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showDetailsModal && selectedMessage && (
          <DetailsModal
            message={selectedMessage}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedMessage(null);
            }}
            onReply={() => {
              setShowDetailsModal(false);
              setShowReplyModal(true);
            }}
            onMarkAsRead={handleMarkAsRead}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showReplyModal && selectedMessage && (
          <ReplyModal
            message={selectedMessage}
            onClose={() => {
              setShowReplyModal(false);
              setSelectedMessage(null);
            }}
            onSuccess={() => {
              fetchMessages();
              fetchStats();
              setShowReplyModal(false);
              setSelectedMessage(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MessagesManagement;
